import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { IviMy26Service } from '../ivi-my26.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * The IviContainerComponent serves as the main container for IVI-related functionality.
 * It initializes the IVI service with query parameters from the route, manages navigation,
 * and sets up the language and other configuration keys required for the IVI business logic.
 *
 * @remarks
 * - Navigates to `/ivi/subscriptions` if not already on that route.
 * - Initializes the IVI service with parameters such as tokens, IDs, VIN, and language code.
 * - Defaults to English (`en-US`) if the provided language code is not supported.
 *
 * @injects
 * - {@link IviMy26Service}: Handles IVI-related business logic and state.
 * - {@link Router}: Used for navigation between routes.
 * - {@link ActivatedRoute}: Accesses route parameters and query params.
 */
@Component({
  selector: 'app-ivi-container',
  templateUrl: './ivi-container.component.html',
  styleUrls: ['./ivi-container.component.scss'],
  standalone: false,
})
export class IviContainerComponent implements OnInit {

  constructor(private iviService: IviMy26Service, private router: Router, private route: ActivatedRoute, private destroyRef: DestroyRef) { }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (!this.initializeIviService(params)) {
        this.router.navigate(['/not-found']);
        return;
      }
      if (this.router.url !== '/ivi/subscriptions') {
        this.router.navigate(['/ivi/subscriptions']);
      }
      this.iviService.setScreen("ivi");
    });
  }

  private initializeIviService(params: ParamMap): boolean {
    /*
    loadIviTriggered:
      true: page can be opened since loadIvi was triggered setting up the necessary data
      false: this means loadIvi was not triggered, hence necessary data won't be set, Should not be allowed to be accessed by inputting URL
    */
    const loadIviTriggered = params.get('loadIviTriggered') === 'true';
    const iviToken = this.getRequiredParam(params, 'iviToken');
    const vin = this.getRequiredParam(params, 'vin');
    const hidasToken = this.getRequiredParam(params, 'hidasToken');

    if (!loadIviTriggered || !iviToken || !vin || !hidasToken) {
      this.iviService.loadIviTriggered = false;
      return false;
    }

    this.iviService.loadIviTriggered = true;
    this.iviService.iviToken = iviToken;
    const languageCode = params.get('languageCode') || 'en-US';
    this.iviService.setPackageKey(params.get('adas')); // this key is specific to default.json configuration.

    //Below parameters are only used for hard coded testing purpose.can be removed once all data is synced up.
    this.iviService.setPoid(params.get('productOwnerId') || '');
    this.iviService.setAccessToken(hidasToken);
    this.iviService.setHidasID(params.get('hidasId') || '');
    this.iviService.setVinNum(vin);
    this.iviService.setUseDefault(params.get('useDefault') || '');
    this.iviService.setGlobalId(params.get('globalId') || '');

    if (!this.isLanguageSupported(languageCode)) {
      this.iviService.languageCode = 'en-US'; // default to English if not supported
    } else {
      this.iviService.languageCode = languageCode;
    }

    return true;
  }

  private getRequiredParam(params: ParamMap, key: string): string {
    return (params.get(key) || '').trim();
  }

  private isLanguageSupported(languageCode: string): boolean {
    // To support additional languages, add their codes to the supportedLanguages array below.
    const supportedLanguages = ['en-US', 'fr-CA', 'en-CA'];
    return supportedLanguages.includes(languageCode);
  }
}